import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';

import { Label } from 'core';
import { currencyFormatter, capitalizeWords, awsDateToDateFormatter } from 'core/helpers/formatters';
import { SortableTable } from 'core/components/sortable-table';
import Switch from 'core/components/switch';
import { Tooltip } from 'core/components/tooltip';
import useStyles from '../payment.styles';

// isRefund comes from the backend mapper; fall back to the re_ prefix for
// payloads served before the field existed
const isRefundRow = (transaction) => transaction.isRefund ?? transaction.id?.startsWith('re_');

const PastPayments = ({ transactions, getBillingPaymentMethod }) => {
  const classes = useStyles();
  const [separateRefunds, setSeparateRefunds] = useState(true);

  const shortenInternalDescription = (internalDescription) => {
    if (internalDescription?.length > 13) {
      return (
        <>
          {`${internalDescription.slice(0, 15)}...`}
          <Tooltip label="See more" text={internalDescription} toggleOnClick />
        </>
      );
    }
    return internalDescription;
  };

  const formatAmount = (_value, transaction) => {
    if (isRefundRow(transaction)) {
      return currencyFormatter(transaction.paymentRefunded);
    }
    return currencyFormatter(transaction.paymentAmount);
  };

  // In the separated view the refund carries its own row, so a charge that was
  // (partially) refunded reads as the successful charge it was.
  const formatStatus = (_value, transaction) => {
    if (!isRefundRow(transaction) && ['refunded', 'partially refunded'].includes(transaction.paymentStatus)) {
      return 'Paid';
    }
    return capitalizeWords(transaction.paymentStatus);
  };

  const formatDate = (_value, transaction) => {
    const date = isRefundRow(transaction) ? transaction.refundedAt : transaction.paymentDate;
    return awsDateToDateFormatter(date);
  };

  const displayData = useMemo(() => {
    if (separateRefunds) {
      return transactions.map((t) => (isRefundRow(t) ? { ...t, paymentDate: t.refundedAt } : t));
    }
    return transactions
      .filter((t) => !isRefundRow(t))
      .map((transaction) => ({
        ...transaction,
        'paymentAmount/paymentRefunded': `${currencyFormatter(transaction.paymentAmount)} / ${currencyFormatter(
          transaction.paymentRefunded
        )}`
      }));
  }, [transactions, separateRefunds]);

  const headers = separateRefunds
    ? [
        ['paymentDate', 'Date'],
        ['paymentAmount', 'Amount'],
        ['paymentMethod', 'Payment Method'],
        ['paymentStatus', 'Payment Status'],
        ['internalDescription', 'Description']
      ]
    : [
        ['paymentDate', 'Date'],
        ['paymentAmount/paymentRefunded', 'Amount/Amount Refunded'],
        ['paymentMethod', 'Payment Method'],
        ['paymentStatus', 'Payment Status'],
        ['internalDescription', 'Description']
      ];

  const formatters = separateRefunds
    ? {
        paymentDate: formatDate,
        paymentAmount: formatAmount,
        paymentStatus: formatStatus,
        internalDescription: shortenInternalDescription,
        paymentMethod: (_p, transaction) =>
          getBillingPaymentMethod(
            transaction.paymentMethod,
            transaction.paymentMethodBrand,
            transaction.paymentMethodLast4,
            classes.tableBodyPaymentMethodLast4
          )
      }
    : {
        paymentDate: awsDateToDateFormatter,
        paymentStatus: capitalizeWords,
        internalDescription: shortenInternalDescription,
        paymentMethod: (_p, transaction) =>
          getBillingPaymentMethod(
            transaction.paymentMethod,
            transaction.paymentMethodBrand,
            transaction.paymentMethodLast4,
            classes.tableBodyPaymentMethodLast4
          )
      };

  return (
    <div className={classes.sectionContainer}>
      <Grid container alignItems="center" justifyContent="space-between" className={classes.rowTitle}>
        <Label type="coverageValue">Past Payments</Label>
        <Switch
          id="separateRefunds"
          label="Separate refunds"
          value={separateRefunds}
          onChange={() => setSeparateRefunds((prev) => !prev)}
          mode="dark"
          className={classes.refundToggle}
        />
      </Grid>
      <Grid container alignItems="flex-start" justifyContent="flex-start">
        {displayData && displayData.length > 0 && (
          <SortableTable
            key={separateRefunds ? 'separated' : 'combined'}
            classes={{
              'dark-tableRowRoot': classes.tableRow,
              'dark-tableCellRoot': classes.tableCell
            }}
            mode="dark"
            dataCy="policies"
            selfContained={false}
            headers={headers}
            data={displayData}
            formatters={formatters}
          />
        )}
      </Grid>
    </div>
  );
};

PastPayments.propTypes = {
  transactions: PropTypes.array.isRequired,
  getBillingPaymentMethod: PropTypes.func.isRequired
};

export default observer(PastPayments);
